import { uniqWith, isEqual } from 'lodash';
import { useEffect } from 'react';
import { useInternalPaginationContext } from 'modules/movableInk/contexts/InternalPagination';
import { useMovableInkBlockContext } from 'modules/movableInk/contexts/MovableInkBlockContext';
import { useMovableInkCampaignContext } from 'modules/movableInk/contexts/MovableInkCampaignContext';
import { useMovableInkIntegrationContext } from 'modules/movableInk/contexts/MovableInkIntegrationContext';
import { usePanelContext } from 'modules/movableInk/contexts/Panel';

export const useMovableInkPanelRefetchEffect = () => {
  const {
    selectedIntegration,
    isFetchingIntegrations,
    setSelectedIntegration,
    integrations,
    currentCompanyId,
    isFetchingChildIntegrations,
    childIntegrations,
    setCurrentCompanyId,
  } = useMovableInkIntegrationContext();
  const { campaignId, refetchCampaignsByTokenId } = useMovableInkCampaignContext();
  const { isFetchingBlocks, blocks, setCachedBlocks, blockPagesTotal, refetchBlocks } =
    useMovableInkBlockContext();
  const { setCurrentPage, currentPage } = useInternalPaginationContext();
  const { debouncedPanelSearch } = usePanelContext();

  // integrations
  useEffect(() => {
    if (!selectedIntegration && !isFetchingIntegrations && !!integrations.length) {
      setSelectedIntegration(integrations[0]);
    }
  }, [integrations, isFetchingIntegrations, selectedIntegration, setSelectedIntegration]);

  // child integrations
  useEffect(() => {
    if (!currentCompanyId && !isFetchingChildIntegrations && !!childIntegrations.length) {
      const defaultCompanyId = childIntegrations.find((child) => child.isDefault)?.companyId;
      if (defaultCompanyId) setCurrentCompanyId(defaultCompanyId);
    }
  }, [childIntegrations, isFetchingChildIntegrations, currentCompanyId, setCurrentCompanyId]);

  // campaigns
  useEffect(() => {
    if (selectedIntegration && !isFetchingIntegrations && currentCompanyId) {
      // fetch campaigns first
      refetchCampaignsByTokenId({
        tokenId: selectedIntegration.tokenId,
        companyId: currentCompanyId,
      });
    }
  }, [selectedIntegration, isFetchingIntegrations, currentCompanyId, refetchCampaignsByTokenId]);

  // blocks
  useEffect(() => {
    if (selectedIntegration && !isFetchingIntegrations && currentCompanyId) {
      refetchBlocks({
        tokenId: selectedIntegration.tokenId,
        campaignId,
        search: debouncedPanelSearch,
        companyId: currentCompanyId,
      });
    }
  }, [
    debouncedPanelSearch,
    campaignId,
    selectedIntegration,
    isFetchingIntegrations,
    currentPage,
    currentCompanyId,
    refetchBlocks,
  ]);

  useEffect(() => {
    if (!isFetchingBlocks && blocks.length && currentPage <= blockPagesTotal) {
      setCachedBlocks((prev) => uniqWith([...prev, ...blocks], isEqual));
    }
  }, [blocks.length, currentPage, isFetchingBlocks, blockPagesTotal]);

  useEffect(() => {
    if (!isFetchingBlocks && !isFetchingIntegrations && !isFetchingChildIntegrations) {
      setCachedBlocks([]);
      setCurrentPage(1);
    }
  }, [campaignId, selectedIntegration, debouncedPanelSearch, currentCompanyId]);
};
